import { useQuery } from '@tanstack/react-query';
import { useAPI } from "/src/hooks/useAPI";

/**
 * Fetch minimal training programs (faster, less data)
 */
const fetchMinimalTrainingPrograms = async (apiBaseUrl, authToken) => {
  const {callAPI} = useAPI();

  if (!authToken || !apiBaseUrl) {
    throw new Error('API base URL and auth token are required');
  }

  try{
    const data = await callAPI(
      `trainingprograms?show_subscribed=true&minimal=true`,
      {
        token: authToken ,
      }
    );
    return data
  }
  catch(error){
    throw new Error('Failed to fetch minimal training programs');
  }
};

/**
 * Fetch full training programs (complete data)
 */
const fetchFullTrainingPrograms = async (apiBaseUrl, authToken) => {
  const {callAPI} = useAPI();
  
  if (!authToken || !apiBaseUrl) {
    throw new Error('API base URL and auth token are required');
  }

  try{
    const data = await callAPI(
      `trainingprograms?show_subscribed=true`,
      {
        token: authToken ,
      }
    );
    return data
  }catch(error){
    throw new Error('Failed to fetch full training programs');
  }

};

/**
 * Custom hook to fetch training programs with React Query
 * First fetches minimal data for fast initial load, then fetches full data
 * Stale time is set to 10 minutes
 * Automatically refetches when data becomes stale
 */
export function useTrainingProgramQuery(apiBaseUrl, authToken, enabled = true, is2FA = false) {
  // First: Fetch minimal data quickly
  const { 
    data: minimalData, 
    isLoading: isLoadingMinimal,
    error: minimalError
  } = useQuery({
    queryKey: ['trainingPrograms', 'minimal', apiBaseUrl, authToken],
    queryFn: () => fetchMinimalTrainingPrograms(apiBaseUrl, authToken),
    enabled: enabled && !!authToken && !!apiBaseUrl && !is2FA,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: true, // Refetch on mount if data is stale
    refetchIntervalInBackground: false,
  });

  // Second: Fetch full data only after minimal data loads
  const { 
    data: fullData, 
    isLoading: isLoadingFull,
    error: fullError
  } = useQuery({
    queryKey: ['trainingPrograms', 'full', apiBaseUrl, authToken],
    queryFn: () => fetchFullTrainingPrograms(apiBaseUrl, authToken),
    enabled: enabled && !!minimalData && !is2FA, // Only fetch when minimal data exists
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: true,
    refetchIntervalInBackground: false,
  });

  // Return full data if available, otherwise minimal data
  return {
    data: fullData || minimalData,
    isLoading: isLoadingMinimal,
    isLoadingFull,
    error: fullError || minimalError,
  };
}
